import { useState } from "react";
import {
  Box, Typography, Paper, Stack, TextField, MenuItem, Alert, Grid2 as Grid,
  Table, TableBody, TableCell, TableHead, TableRow, useTheme,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { useQuery } from "@tanstack/react-query";
import { diagnosticsApi, type DxQcLevel, type DxLjPointDto } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";

const LEVELS: ("" | DxQcLevel)[] = ["", "Low", "Normal", "High"];

/**
 * Levey-Jennings QC chart.
 *
 * Colours come from the theme rather than fixed hex: the axis and grid literals this used to carry
 * were near-invisible on the dark canvas, and the point colours encode pass/warning/fail, which is
 * exactly what the semantic palette is for.
 */
function LeveyJenningsChart({ points }: { points: DxLjPointDto[] }) {
  const theme = useTheme();
  const W = 680, H = 240, padL = 36, padR = 12, padT = 12, padB = 20;
  const innerW = W - padL - padR, innerH = H - padT - padB, midY = padT + innerH / 2;
  const data = [...points].reverse(); // API returns newest-first → plot oldest→newest
  const zToY = (z: number) => midY - (Math.max(-4, Math.min(4, z)) / 4) * (innerH / 2);
  const xAt = (i: number) => padL + (data.length <= 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const color = (o: string) =>
    o === "Fail" ? theme.palette.error.main
    : o === "Warning" ? theme.palette.warning.main
    : theme.palette.success.main;
  const axis = theme.palette.text.secondary;
  const grid = theme.palette.divider;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }}>
      <rect x={padL} y={padT} width={innerW} height={innerH} fill="none" stroke={grid} />
      {[-3, -2, -1, 0, 1, 2, 3].map(sd => (
        <g key={sd}>
          <line x1={padL} y1={zToY(sd)} x2={W - padR} y2={zToY(sd)} stroke={sd === 0 ? axis : grid} strokeDasharray={sd === 0 ? "" : "3 3"} />
          <text x={4} y={zToY(sd) + 3} fontSize={9} fill={axis}>{sd > 0 ? `+${sd}` : sd}SD</text>
        </g>
      ))}
      {data.length > 1 && <polyline fill="none" stroke={axis} strokeWidth={1} points={data.map((p, i) => `${xAt(i)},${zToY(Number(p.zScore))}`).join(" ")} />}
      {data.map((p, i) => <circle key={i} cx={xAt(i)} cy={zToY(Number(p.zScore))} r={3} fill={color(p.outcome)} />)}
    </svg>
  );
}

export function DiagnosticsAnalyticsHubPage() {
  const [analyte, setAnalyte] = useState(""); const [level, setLevel] = useState<"" | DxQcLevel>("");
  const lj = useQuery({ queryKey: ["dx-lj", analyte, level], queryFn: () => diagnosticsApi.leveyJennings({ analyte: analyte || undefined, level: level || undefined }) });
  const util = useQuery({ queryKey: ["dx-modality-util"], queryFn: () => diagnosticsApi.modalityUtilization() });
  const prod = useQuery({ queryKey: ["dx-rad-productivity"], queryFn: () => diagnosticsApi.radiologistProductivity() });
  const rev = useQuery({ queryKey: ["dx-revenue"], queryFn: () => diagnosticsApi.diagnosticRevenue() });
  const rej = useQuery({ queryKey: ["dx-rejection-kpis"], queryFn: () => diagnosticsApi.rejectionKpis() });

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Reports & analytics" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Levey-Jennings (QC)</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField label="Analyte" size="small" value={analyte} onChange={e => setAnalyte(e.target.value)} placeholder="e.g. Glucose" />
              <TextField select label="Level" size="small" value={level} onChange={e => setLevel(e.target.value as "" | DxQcLevel)} sx={{ width: 130 }}>
                {LEVELS.map(l => <MenuItem key={l} value={l}>{l || "All"}</MenuItem>)}
              </TextField>
            </Stack>
            {lj.isError && <Alert severity="error">{errorMessage(lj.error)}</Alert>}
            {(lj.data?.length ?? 0) === 0 ? <Typography variant="caption" color="text.secondary">No QC points{analyte ? ` for ${analyte}` : ""}.</Typography> : <LeveyJenningsChart points={lj.data ?? []} />}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Modality utilization</Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Modality</TableCell><TableCell align="right">Studies</TableCell><TableCell align="right">Slots (booked/total)</TableCell><TableCell align="right">Util %</TableCell></TableRow></TableHead>
              <TableBody>{(util.data ?? []).map(m => <TableRow key={m.modalityId}><TableCell>{m.modalityName}</TableCell><TableCell align="right">{m.studyCount}</TableCell><TableCell align="right">{m.bookedSlots}/{m.totalSlots}</TableCell><TableCell align="right">{m.slotUtilizationPct}%</TableCell></TableRow>)}</TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Radiologist productivity</Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Radiologist</TableCell><TableCell align="right">Reports approved</TableCell><TableCell align="right">Avg reporting (min)</TableCell></TableRow></TableHead>
              <TableBody>{(prod.data ?? []).map((r, i) => <TableRow key={i}><TableCell>{r.radiologist}</TableCell><TableCell align="right">{r.reportsApproved}</TableCell><TableCell align="right">{r.avgReportingMinutes}</TableCell></TableRow>)}</TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Rejected samples (KPIs)</Typography>
            {rej.isError && <Alert severity="error" sx={{ mb: 1 }}>{errorMessage(rej.error)}</Alert>}
            {rej.data && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Rejection rate: <b>{rej.data.rejectionRatePct}%</b> · Recollection rate: <b>{rej.data.recollectionRatePct}%</b> · Rejected {rej.data.rejectedSamples}/{rej.data.totalSamples}
              </Typography>
            )}
            <Table size="small">
              <TableHead><TableRow><TableCell>Reason</TableCell><TableCell align="right">Count</TableCell></TableRow></TableHead>
              <TableBody>{(rej.data?.byReason ?? []).map((r, i) => <TableRow key={i}><TableCell>{r.reason}</TableCell><TableCell align="right">{r.count}</TableCell></TableRow>)}</TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Revenue</Typography>
            {rev.data && <Typography variant="body2" sx={{ mb: 1 }}>Billed orders: <b>{rev.data.billedOrders}</b> · Total: <b>{rev.data.totalRevenue}</b></Typography>}
            <Table size="small">
              <TableHead><TableRow><TableCell>Order type</TableCell><TableCell align="right">Orders</TableCell><TableCell align="right">Revenue</TableCell></TableRow></TableHead>
              <TableBody>{(rev.data?.byOrderType ?? []).map(t => <TableRow key={t.orderType}><TableCell>{t.orderType}</TableCell><TableCell align="right">{t.orders}</TableCell><TableCell align="right">{t.revenue}</TableCell></TableRow>)}</TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
